import { useState } from 'react';
import { Grid, IconButton, Tooltip } from '@mui/material';
import Table from '../../components/Table';
import CustomButton from 'components/Button';
import AddIcon from '@mui/icons-material/Add';
import SuccessError from 'components/SuccessError';
import {
  isAdmin,
  isUser,
  isAreaAdmin,
  isSuperAdmin,
  tableOptionFilter,
  updateHistoryState
} from 'utils/utils';
import Loader from 'components/Loader';
import { useSelector, useDispatch } from 'react-redux';
import { authPostRequest, getAuthRequest } from 'Api/api';
import { useEffect } from 'react';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import EditIcon from '@mui/icons-material/Edit';
import CreateAndUpdateStore from './CreateAndUpdateStore';
import ConfirmationBox from 'components/ConfirmationBox';
import { addAdminAccess, addStore } from 'reduxStore/authSlice';
import { useNavigate } from 'react-router-dom';
import LoginIcon from '@mui/icons-material/Login';
import TextEllipsize from 'utils/TextEllipsize';
import TextHighlightSpan from 'components/TextHighlightSpan';
import SuperAdminView from 'pages/adminsView/SuperAdminView';
import Disclaimer from 'components/Disclaimer';
import { getStoreLogoPreviewUrl } from './storeUtils';

const columnOptions = {
  setCellProps: () => ({ style: { maxWidth: '210px' } }),
  customBodyRender: (value) => <TextEllipsize value={value} length={20} />
};
const columns = [
  {
    name: 'storeName',
    label: 'Name',
    options: {
      filter: true,
      sort: false,
      ...columnOptions
    }
  },

  {
    name: 'contactName',
    label: 'Contact Name',
    options: {
      filter: true,
      sort: false,
      ...columnOptions
    }
  },

  {
    name: 'email',
    label: 'Email',
    options: {
      filter: true,
      sort: false,
      ...columnOptions
    }
  },

  {
    name: 'phone1',
    label: 'Phone',
    options: {
      filter: true,
      sort: false,
      setCellProps: () => ({ style: { maxWidth: '210px' } }),
      customBodyRender: (value) => <TextEllipsize value={value} length={10} />
    }
  },

  {
    name: 'currentTierPercentage',
    label: 'Current Tier %',
    options: {
      filter: true,
      sort: false,
      customBodyRender: (value) => {
        return value != null && value;
      }
    }
  },

  {
    name: 'address',
    label: 'Address',
    options: {
      filter: true,
      sort: false,
      ...columnOptions
    }
  }
];

const Stores = () => {
  const [addOpenModal, setAddOpenModal] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const [isFormSaved, setIsFormSaved] = useState(false);
  const [successMessage, setSuccessMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [openConfirmationBox, setOpenConfirmationBox] = useState(false);
  const [store, setStore] = useState();
  const [stores, setStores] = useState();
  const [editMode, setEditMode] = useState();

  const { userData, adminAccess, showDisclaimer } = useSelector((store) => store.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const historyTrack = localStorage.getItem('historyTrack');
  const logoutStoreHistory = localStorage.getItem('logoutStoreHistory');

  useEffect(() => {
    if (historyTrack || logoutStoreHistory) {
      // history.replaceState(null, '', window.location.href);
      // const newURL = window.location.pathname + window.location.search + window.location.hash;
      // history.replaceState({}, '', newURL);

      // Replace the current state multiple times to clear the history
      // for (let i = 0; i < history.length; i++) {
      //   // history.replaceState(null, '', window.location.href);
      //   // history.replaceState({}, '', newURL);
      // }
      localStorage.removeItem('historyTrack');
      localStorage.removeItem('logoutStoreHistory');
    }

    if (history.state == null) {
      history.pushState(null, '', '/Stores');
      navigate('/Stores');
      updateHistoryState();
    }
  }, [historyTrack]);

  const handleConfirmationOpen = (store) => {
    setStore(stores[store]);
    setOpenConfirmationBox(true);
  };

  const handleOpenModal = (store) => {
    setStore(stores[store]);
    setAddOpenModal(true);
    setEditMode(true);
  };

  const handleDisableEnableStore = async (active) => {
    try {
      const params = {
        userId: userData.userId,
        storeId: store?.id
      };
      setShowLoader(true);
      setShowAlertBar(false);

      const storeStatus = active
        ? await authPostRequest('disableStore', params)
        : await authPostRequest('enableStore', params);
      setShowLoader(false);
      if (storeStatus.resStatus === 1) {
        setIsFormSaved(!isFormSaved);
        setSuccessMessage([storeStatus.resMessage]);
      } else {
        setErrorMessage([storeStatus.resMessage]);
      }

      setShowAlertBar(true);
      setOpenConfirmationBox(!ConfirmationBox);
      window.scrollTo(0, 0);
    } catch (err) {
      setErrorMessage(['Error']);
      window.scrollTo(0, 0);
    }
  };

  const viewAdminStore = async (storeIndex) => {
    const selectedStore = stores[storeIndex];
    const logoPreviewUrl = await getStoreLogoPreviewUrl(selectedStore?.id);

    dispatch(addStore({ ...selectedStore, storeLogoURL: logoPreviewUrl }));
    dispatch(addAdminAccess(selectedStore?.id));
    setShowLoader(true);

    localStorage.setItem('BeforeLoginStoreHistory', true);
    navigate('/StoreInfo/projects');

    setShowLoader(false);
  };

  const getStores = async () => {
    try {
      setShowLoader(true);
      const storesList = await getAuthRequest('getStoresList');

      setStores(storesList?.storesList);
      setShowLoader(false);
    } catch (err) {
      setShowLoader(false);
      console.log(err);
    }
  };

  const getUserStores = async () => {
    try {
      const requestParams = {
        userId: userData?.userId
      };
      setShowLoader(true);
      const storesList = await authPostRequest('getUserStores', requestParams);
      setStores(storesList?.storesList);
      setShowLoader(false);
    } catch (err) {
      setShowLoader(false);
      return err;
    }
  };

  useEffect(() => {
    if (isAdmin(userData?.roles) || isUser(userData?.roles)) {
      if (adminAccess) {
        // navigate('/Error');
        navigate('/storeInfo/projects');
      } else navigate('/storeInfo/projects');
      // navigate('/403');
    }
    if (isSuperAdmin(userData.roles)) {
      getStores();
    }

    if (isAreaAdmin(userData.roles)) {
      getUserStores();
    }
  }, [isFormSaved, adminAccess, showDisclaimer]);

  return (
    <>
      <SuperAdminView />
      <Grid display="flex" alignItems="center" justifyContent="flex-end">
        {showAlertBar && (
          <SuccessError
            successMessage={successMessage}
            errorMessage={errorMessage}
            setShowAlertBar={setShowAlertBar}
          />
        )}
        {isSuperAdmin(userData.roles) && (
          <CustomButton
            add={true}
            type="submit"
            endIcon={<AddIcon />}
            onClick={() => {
              setStore({
                storeName: '',
                contactName: '',
                email: '',
                phone1: '',
                phone2: '',
                address: ''
              });
              setAddOpenModal(true);
            }}>
            Add
          </CustomButton>
        )}
      </Grid>

      <Table
        data={stores}
        options={tableOptionFilter}
        columns={[
          ...columns,
          {
            name: 'Actions',
            lable: 'Actions',
            options: {
              filter: false,
              sort: false,
              setCellProps: () => ({ style: { maxWidth: '300px' } }),
              // Delete and Update Actions
              customBodyRender: (value, tableMeta) => {
                return (
                  <Grid sx={{ ml: '-1.5vh' }}>
                    <>
                      {isSuperAdmin(userData.roles) && (
                        <>
                          {stores[tableMeta.rowIndex].active && (
                            <Tooltip title="Update">
                              <IconButton onClick={() => handleOpenModal(tableMeta.rowIndex)}>
                                <EditIcon style={{ color: '#040273' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip
                            title={stores[tableMeta.rowIndex].active ? 'Enabled' : 'Disabled'}>
                            <IconButton onClick={() => handleConfirmationOpen(tableMeta.rowIndex)}>
                              {stores[tableMeta.rowIndex].active ? (
                                <ToggleOnIcon style={{ color: 'green' }} />
                              ) : (
                                <ToggleOffIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      {stores[tableMeta.rowIndex].active && (
                        <Tooltip title="Login to Store">
                          <IconButton onClick={() => viewAdminStore(tableMeta.rowIndex)}>
                            <LoginIcon style={{ color: '#040273' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  </Grid>
                );
              }
            }
          }
        ]}
      />

      {!showDisclaimer && showLoader && <Loader open={showLoader} />}

      {openConfirmationBox && (
        <ConfirmationBox
          openConfirmationBox={openConfirmationBox}
          setOpenConfirmationBox={setOpenConfirmationBox}
          handleYes={() => handleDisableEnableStore(store?.active)}
          message={
            store?.active ? (
              <TextHighlightSpan
                messageStart="If you disable the "
                messageEnd=", all store users will be unable to log in. 
              Are you sure you want to proceed?"
                spanText={store.storeName}
              />
            ) : (
              <TextHighlightSpan
                messageStart="If you enable the "
                messageEnd=", all store users will be able to log in. 
              Are you sure you want to proceed?"
                spanText={store.storeName}
              />
            )
          }
          title={store?.active ? 'Disable?' : 'Enable?'}
        />
      )}

      {addOpenModal && (
        <CreateAndUpdateStore
          store={store}
          addOpenModal={addOpenModal}
          editMode={editMode}
          setAddOpenModal={setAddOpenModal}
          setSuccess={setSuccessMessage}
          setAlertBar={setShowAlertBar}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
        />
      )}

      {showDisclaimer && (
        <Disclaimer
          setErrorMessage={setErrorMessage}
          setSuccessMessage={setSuccessMessage}
          setShowAlertBar={setShowAlertBar}
        />
      )}
    </>
  );
};

export default Stores;
